import React, { useEffect, useRef } from 'react';
import PageWrapper from 'Pages/PageWrapper';
import SignIn from 'Pages/SignIn';
import { matchPath, Redirect, Route, Switch } from 'react-router-dom';
import { PrivateRoute } from './PrivateRoute';
import { Location } from 'history';
import { withSkin, SKIN_ROUTE_PATTERN } from '~/router/solutionUrl';

/**
 * TODO
 * - Transition blocking when leaving from dirty form
 * - Authorization: admin, manager, trader, viewer
 * - Flag based routing, dynamic routing, feature toggle
 */

export const UUID =
  '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

export const ORDERPID = '[BO]-[A-Z0-9]{8}-\\d+';

const withSkinUUID = (path: string, param: string) => [
  `/${SKIN_ROUTE_PATTERN}${path.replace(`:${param}`, `:${param}(${UUID})`)}`,
  path.replace(`:${param}`, `:${param}(${UUID})`),
];
const withSkinPattern = (path: string, param: string, pattern: string) => [
  `/${SKIN_ROUTE_PATTERN}${path.replace(`:${param}`, `:${param}(${pattern})`)}`,
  path.replace(`:${param}`, `:${param}(${pattern})`),
];

export const PRIVATE_ROUTES = {
  // Landing page (home page)
  landing: '/',
  // Solution home (previously was '/')
  solutionHome: `/${SKIN_ROUTE_PATTERN}`,

  // Legacy /home — redirected in PageWrapper
  home: '/home',

  // Routes with skin prefix (accept both patterns for backward compat)
  overview: withSkin('/overview'),
  marketplace: withSkin('/marketplace'),
  marketdepth: withSkinUUID('/marketplace/:productId', 'productId'),

  dashboard: withSkin('/dashboard'),

  products: withSkin('/products'),
  product: withSkinUUID('/products/:id/quick', 'id'),
  productSpecifications: withSkinUUID('/products/:id/specifications', 'id'),

  tenders: withSkin('/tenders'),

  orders: withSkin('/orders'),
  orderNew: withSkinPattern('/orders/:orderType', 'orderType', 'bid|offer'),
  orderEdit: withSkinPattern('/orders/:orderId/edit', 'orderId', ORDERPID),
  liquidityProvider: withSkin('/lp-overview'),
  trades: withSkin('/trades'),

  account: withSkin('/account'),

  config: withSkin('/config'),

  routes: withSkin('/routes'),

  participants: withSkin('/participants'),
  locations: withSkin('/locations'),
  customers: withSkin('/customers'),
  consignees: withSkin('/partners'),
  customerRequest: withSkin('/customer-requests'),
  reports: withSkin('/reports'),

  consolidation: withSkin('/consolidation'),
  deliveries: withSkin('/deliveries'),
  users: withSkin('/users'),

  futures: withSkin('/futures'),
  duplicate: withSkin('/duplicate'), // duplicate (tender) orders
};

// Flatten routes for path matching (extracts first pattern from arrays)
export const PRIVATE_ROUTES_FLAT = Object.fromEntries(
  Object.entries(PRIVATE_ROUTES).map(([key, value]) => [
    key,
    Array.isArray(value) ? value[0] : value,
  ])
) as Record<keyof typeof PRIVATE_ROUTES, string>;

export const ROUTES = {
  signIn: '/signin',
  loginEmail: '/login/email',
  ...PRIVATE_ROUTES,
};

export function checkIsModal(pathname: string) {
  const modalRoutes = [
    withSkin('/preview'),
    ROUTES.orderNew,
    ROUTES.orderEdit,
    ROUTES.marketplace,
    ROUTES.marketdepth,
    ROUTES.product,
    ROUTES.productSpecifications,
    ROUTES.account,
    ROUTES.futures,
    ROUTES.duplicate,
  ].flat(); // Flatten arrays from withSkin helpers
  return modalRoutes.some((path) => matchPath(pathname, { path }));
}

export function checkIsTradeModal(pathname: string) {
  return matchPath<{ id: string }>(pathname, {
    path: withSkin('/preview/trade/:id'),
  });
}

export function usePreviousNormalLocation(value: Location) {
  const ref = useRef<Location>();
  useEffect(() => {
    if (!checkIsModal(value.pathname)) {
      ref.current = value;
    }
  });
  return ref.current;
}

interface RoutesProps {
  user: unknown;
}

// Flatten all private routes for route matching
const ALL_PRIVATE_PATHS = Object.values(PRIVATE_ROUTES).flat();

export function Routes({ user }: RoutesProps) {
  return (
    <Switch>
      {/* Sign-in / login are global (never skinned). A prefixed deep link like
          /hilmar/signin#signout would otherwise fall through to PrivateRoute
          (via the /:skin solution-home pattern) and hang on the loader. Strip
          the leading segment back to the canonical auth path (preserving hash
          and search). The route shape guarantees a segment before signin/login,
          so the stripped path always differs — no same-path redirect loop. */}
      <Route
        path={[
          `/${SKIN_ROUTE_PATTERN}${ROUTES.signIn}`,
          `/${SKIN_ROUTE_PATTERN}${ROUTES.loginEmail}`,
        ]}
        render={({ location }) => (
          <Redirect
            to={{
              ...location,
              pathname: location.pathname.replace(/^\/[^/]+/, ''),
            }}
          />
        )}
      />
      <Route path={[ROUTES.signIn, ROUTES.loginEmail]} component={SignIn} />
      <PrivateRoute
        path={ALL_PRIVATE_PATHS}
        component={PageWrapper}
        user={user}
      />
    </Switch>
  );
}
